import DOMPurify from "dompurify";
import styled from "styled-components";

// Formatting + the content-kit blocks. `class` is allowed so the kit's section
// classes survive; scripts/handlers/inline styles are stripped.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    "p",
    "br",
    "strong",
    "b",
    "em",
    "i",
    "u",
    "s",
    "ul",
    "ol",
    "li",
    "a",
    "h1",
    "h2",
    "h3",
    "h4",
    "blockquote",
    "div",
    "span",
    "code",
    "pre",
    "hr",
    "img",
    "dl",
    "dt",
    "dd",
    "figure",
    "figcaption",
  ],
  ALLOWED_ATTR: ["href", "target", "rel", "class", "src", "alt"],
};

let hookAdded = false;
const ensureHook = () => {
  if (hookAdded || typeof window === "undefined") return;
  DOMPurify.addHook("afterSanitizeAttributes", (node: any) => {
    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
  hookAdded = true;
};

// Scoped stylesheet for the modal's dynamic (authored) body content — a
// content kit of composable blocks. `$accent` is the notification's type color,
// driving the "brand" accents (links, step/check markers, CTA, quote rule).
// All grays and the callout/change semantic colors use theme CSS vars
// (radix scales) so the body reads correctly in light and dark mode.
const Prose = styled.div<{ $accent: string }>`
  font-family: inherit;
  font-size: 15px;
  color: var(--colorsGray11);
  line-height: 1.6;
  overflow-wrap: break-word;

  & > :first-child {
    margin-top: 0;
  }
  & > :last-child {
    margin-bottom: 0;
  }

  /* text */
  p {
    margin: 0 0 18px;
  }
  b,
  strong {
    color: var(--colorsGray12);
    font-weight: 700;
  }
  em,
  i {
    font-style: italic;
  }
  a {
    color: ${(props) => props.$accent};
    font-weight: 600;
    text-decoration: underline;
  }
  .lead {
    font-size: 16px;
    color: var(--colorsGray12);
  }
  .muted {
    font-size: 13.5px;
    color: var(--colorsGray10);
  }

  /* headings */
  h2 {
    font-size: 17px;
    font-weight: 700;
    color: var(--colorsGray12);
    letter-spacing: -0.01em;
    margin: 0 0 12px;
  }
  h3 {
    font-family: inherit;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--colorsGray10);
    margin: 0 0 14px;
  }

  /* numbered steps (absolute marker so inline bold wraps cleanly) */
  ol.steps {
    list-style: none;
    margin: 0 0 22px;
    padding: 0;
    counter-reset: step;
  }
  ol.steps li {
    position: relative;
    padding-left: 39px;
    min-height: 26px;
    margin: 0 0 14px;
    font-size: 14px;
    line-height: 1.5;
  }
  ol.steps li:last-child {
    margin-bottom: 0;
  }
  ol.steps li::before {
    counter-increment: step;
    content: counter(step);
    position: absolute;
    left: 0;
    top: 0;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: ${(props) => props.$accent}1a;
    color: ${(props) => props.$accent};
    font-weight: 700;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* plain lists */
  ul {
    list-style: disc;
    margin: 0 0 18px;
    padding-left: 20px;
  }
  ul li {
    margin: 0 0 6px;
  }
  ol {
    list-style: decimal;
    margin: 0 0 18px;
    padding-left: 20px;
  }

  /* check / feature list */
  ul.checks {
    list-style: none;
    margin: 0 0 22px;
    padding: 0;
  }
  ul.checks li {
    position: relative;
    padding-left: 33px;
    min-height: 22px;
    margin: 0 0 10px;
    font-size: 14px;
    line-height: 1.5;
  }
  ul.checks li:last-child {
    margin-bottom: 0;
  }
  ul.checks li::before {
    content: "✓";
    position: absolute;
    left: 0;
    top: 0;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: ${(props) => props.$accent}1a;
    color: ${(props) => props.$accent};
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* callouts — info / success / warning / error.
     The base .callout defaults to the amber "note" look with a ⚠ icon, so even
     a bare <div class="callout">…</div> (no variant class, no .ico span) still
     renders as a proper callout. Variant classes override the colors; an author
     <span class="ico">…</span> overrides the default icon.
     The icon is absolutely positioned (not a flex child) so the author's text
     and any inline tags flow as normal copy even when not wrapped in a <div>
     (display:flex used to split loose text + <strong> into two columns). */
  .callout {
    position: relative;
    border: 1px solid var(--colorsOrange6);
    background: var(--colorsOrange3);
    color: var(--colorsOrange11);
    border-radius: 10px;
    padding: 13px 14px 13px 38px;
    margin: 0 0 18px;
    font-size: 13.5px;
    line-height: 1.5;
  }
  .callout .ico,
  .callout:not(:has(.ico))::before {
    position: absolute;
    left: 13px;
    top: 12px;
    font-size: 15px;
    line-height: 1.4;
  }
  /* default icon when the author didn't supply a <span class="ico"> */
  .callout:not(:has(.ico))::before {
    content: "⚠";
  }
  /* a bare <div> child (the common wrapper) is just normal block flow */
  .callout > div {
    margin: 0;
  }
  .callout.info {
    background: var(--colorsBlue3);
    border-color: var(--colorsBlue6);
    color: var(--colorsBlue11);
  }
  .callout.success {
    background: var(--colorsGreen3);
    border-color: var(--colorsGreen6);
    color: var(--colorsGreen11);
  }
  .callout.warning {
    background: var(--colorsOrange3);
    border-color: var(--colorsOrange6);
    color: var(--colorsOrange11);
  }
  .callout.error {
    background: var(--colorsRed3);
    border-color: var(--colorsRed6);
    color: var(--colorsRed11);
  }
  .callout b,
  .callout strong,
  .callout a {
    color: inherit;
  }

  /* key / value detail rows */
  dl.details {
    margin: 0 0 20px;
    padding: 0;
    border: 1px solid var(--colorsGray6);
    border-radius: 10px;
    overflow: hidden;
  }
  dl.details > div {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 11px 14px;
    border-top: 1px solid var(--colorsGray6);
    font-size: 14px;
  }
  dl.details > div:first-child {
    border-top: 0;
  }
  dl.details dt {
    margin: 0;
    color: var(--colorsGray11);
  }
  dl.details dd {
    margin: 0;
    color: var(--colorsGray12);
    font-weight: 600;
    text-align: right;
  }

  /* before / after pills */
  .change {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0 0 20px;
    font-size: 14px;
  }
  .change .pill {
    padding: 5px 11px;
    border-radius: 8px;
    font-weight: 600;
  }
  .change .old {
    background: var(--colorsGray3);
    color: var(--colorsGray10);
    text-decoration: line-through;
  }
  .change .new {
    background: var(--colorsGreen3);
    color: var(--colorsGreen11);
  }
  .change .arrow {
    color: var(--colorsGray9);
  }

  /* inline CTA link-button */
  .cta {
    display: inline-block;
    margin: 0 0 20px;
    padding: 10px 18px;
    border-radius: 8px;
    background: ${(props) => props.$accent};
    color: #fff;
    font-weight: 700;
    font-size: 14px;
    text-decoration: none;
  }

  /* image / figure */
  img {
    display: block;
    max-width: 100%;
    height: auto;
    border-radius: 10px;
  }
  figure {
    margin: 0 0 20px;
  }
  figure img {
    width: 100%;
  }
  figure figcaption {
    margin-top: 7px;
    font-size: 12.5px;
    color: var(--colorsGray10);
    text-align: center;
  }
  .img-placeholder {
    width: 100%;
    aspect-ratio: 16 / 7;
    border-radius: 10px;
    background: repeating-linear-gradient(
      45deg,
      var(--colorsGray3),
      var(--colorsGray3) 9px,
      var(--colorsGray4) 9px,
      var(--colorsGray4) 18px
    );
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--colorsGray9);
    font-size: 13px;
  }

  /* divider + quote */
  hr {
    border: 0;
    border-top: 1px solid var(--colorsGray6);
    margin: 20px 0;
  }
  blockquote {
    margin: 0 0 20px;
    padding: 2px 0 2px 14px;
    border-left: 3px solid ${(props) => props.$accent};
    color: var(--colorsGray11);
    font-size: 14.5px;
  }

  code {
    font-family: monospace;
    background: var(--colorsGray3);
    padding: 0.1em 0.3em;
    border-radius: 4px;
  }
  pre {
    background: var(--colorsGray3);
    padding: 0.75em;
    border-radius: 8px;
    overflow-x: auto;
  }
`;

export const SanitizedHtml = ({
  html,
  className,
  linkColor = "#2f8fef",
}: {
  html: string;
  className?: string;
  linkColor?: string;
}) => {
  // DOMPurify needs a DOM; render nothing during SSR (the modal only mounts on
  // a client interaction anyway).
  if (typeof window === "undefined") return null;
  ensureHook();
  const clean = DOMPurify.sanitize(html, SANITIZE_CONFIG);
  return (
    <Prose
      $accent={linkColor}
      className={className}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
};
